import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, AlertTriangle, AlertCircle, CheckCircle2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000; // 60 sn — daha sık çağırmaya değmez

const SEVERITY_META = {
  info: { color: 'text-sky-400', icon: Info },
  success: { color: 'text-emerald-400', icon: CheckCircle2 },
  warning: { color: 'text-amber-400', icon: AlertTriangle },
  danger: { color: 'text-red-400', icon: AlertCircle },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'şimdi';
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
}

/**
 * Header'da yer alan bildirim zili. 60 sn'lik polling ile unread count'u
 * çeker; tıklayınca son 50 bildirim dropdown'unu açar. Tek kullanıcılık
 * çağrı, naturally tenant-safe (server-side filter user_id == caller).
 */
export default function NotificationBell({ collapsed = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread_count: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const fetchInbox = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/notifications/inbox?limit=50`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (r.ok) setData(await r.json());
    } catch {
      // sessiz başarısızlık — bell'in her hatasında kullanıcıyı uyarmak istemiyoruz
    }
  }, []);

  // İlk yükleme + polling
  useEffect(() => {
    fetchInbox();
    const id = setInterval(fetchInbox, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchInbox]);

  // Dışarı tıklayınca kapat
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Smart positioning: butonun ekrandaki konumuna göre dropdown'u
  // yukarı/aşağı + sola/sağa hizala. Bell hem desktop sidebar bottom'da
  // hem de mobile header'da görünür — sabit `mt-2 right-0` her ikisinde de
  // çalışmıyordu (sidebar'da dropdown ekran dışına taşıyordu).
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const W = 340;
    const H = 460;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Yatay: ekranın sol yarısındaysa dropdown sağa açılır, değilse sola
    const left = r.left < vw / 2
      ? Math.min(r.right + margin, vw - W - margin)
      : Math.max(r.left - W - margin, margin);
    // Dikey: alt yarıdaysa yukarı doğru aç, üstteyse aşağı doğru
    const top = r.top > vh / 2
      ? Math.max(r.bottom - H, margin)
      : Math.min(r.top, vh - H - margin);
    setDropdownStyle({
      position: 'fixed',
      left: `${left}px`,
      top: `${Math.max(margin, top)}px`,
      width: `${W}px`,
      maxHeight: `${H}px`,
    });
  }, [open]);

  const handleItemClick = async (item) => {
    if (!item.is_read) {
      try {
        await fetch(`${API_URL}/api/notifications/inbox/${item.id}/read`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
      } catch { /* sessiz */ }
    }
    setOpen(false);
    if (item.link) navigate(item.link);
    fetchInbox();
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/notifications/inbox/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      await fetchInbox();
    } finally {
      setLoading(false);
    }
  };

  const unread = data.unread_count;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        className={`relative flex items-center gap-2 ${collapsed ? 'justify-center' : ''} p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors`}
        aria-label="Kişisel bildirimler"
        title="Size özel bildirimler (sistem olayları, yaklaşan sertifika, davet teyidi)"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {!collapsed && <span className="text-xs">Bildirim</span>}
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center"
                data-testid="notification-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="z-50 rounded-xl border border-border bg-background shadow-xl overflow-hidden flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-border">
            <div className="text-sm font-semibold">Kişisel Bildirimler</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                className="text-[11px] text-primary hover:underline flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-3 h-3" />Tümünü okundu işaretle
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-10">
                Henüz bildiriminiz yok
              </div>
            ) : (
              data.items.map(item => {
                const meta = SEVERITY_META[item.severity] || SEVERITY_META.info;
                const Icon = meta.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left px-4 py-3 border-b border-border/40 hover:bg-muted/40 transition-colors flex gap-3 ${
                      !item.is_read ? 'bg-primary/5' : ''
                    }`}
                    data-testid={`notif-${item.id}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className={`text-sm ${!item.is_read ? 'font-semibold' : 'font-normal'} truncate`}>
                          {item.title}
                        </div>
                        {!item.is_read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                      </div>
                      <div className="text-[11px] text-muted-foreground line-clamp-2">{item.body}</div>
                      <div className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo(item.created_at)}</div>
                    </div>
                    {item.is_read && <Check className="w-3 h-3 text-emerald-500/40 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
